import {Box, Flex, Heading, Image, Text} from "@chakra-ui/react";
import NoCover from "../../components/NoCover/NoCover";
import img6 from "../../assets/images/img-6.png";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {BASE_URL} from "../../config";
import FavoriteButton from "../AllEvents/FavoriteButton";
import {isFavorite} from '../../utils/cookie-utils';
import SharePopover from "../../components/share/SharePopover";
import {getCleanDescription} from "../../utils/htmlUtils";
import BuyTicketButton from "./BuyTicketButton";
import CategoryTag from "../../components/common/CategoryTag";
import { getPriceLabel } from "../../components/helpers/TicketHelpers";

// Ikona w zaokrąglonym jasnym kwadracie (nowy design)
const IconBadge = ({ children }) => (
    <Flex w="36px" h="36px" borderRadius="10px" bg="#F6EFEF" alignItems="center" justifyContent="center" flexShrink={0}>
        {children}
    </Flex>
);

const EventDetailHeaderPage = ({event, social = true, onFavoriteToggle, onImageClick, onBuyClick}) => {
    const navigate = useNavigate();
    const {t, i18n} = useTranslation("default");
    const baseURL = BASE_URL;

    const handleNavigate = (route, options = {}) => {
        navigate(route, options);
    };

    const handleEvents = () => {
        handleNavigate("/");
    };

    const formattedDate = (date) => {
        if(!date) return '';
        const formattedDate = new Intl.DateTimeFormat('pl-PL', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        }).format(date);
        return formattedDate;
    }

    const eventDate = (event) => {
        if(!event?.startDate) return '';
        if( event.startDate === event.endDate ) {
            return `${formattedDate(new Date(event.startDate))}, ${event.startTime}–${event.endTime}`;
        }
        return `${formattedDate(new Date(event.startDate))}, ${event.startTime} – ${formattedDate(new Date(event.endDate))}, ${event.endTime}`;
    }

    const getImg = (event) => {
        const path = event?.coverImage;
        return path ? `${baseURL.replace(/\/$/, '')}/${path.replace(/^\//, '')}` : null;
    }

    const socialButtons = social && (
        <Flex gap="10px" flexShrink={0}>
            <SharePopover
                url={window.location.href}
                shareUrl={`https://evenado.com/share/event/${event.id}`}
                title={event.title}
                description={getCleanDescription(event.description)}
                image={getImg(event)}
            />
            <FavoriteButton eventId={event.id} isFavorite={isFavorite(event.id)} onChange={onFavoriteToggle} variant="light"/>
        </Flex>
    );

    return <Flex bg="surface.card" color="ink.600" w={"100%"} alignItems={"center"} justifyContent="center" borderBottom="1px solid" borderColor="surface.border">
        <Flex flexDirection={'column'} gap={'16px'} w="100%" maxW="7xl" px={{ base: '16px', md: '24px', '2xl': 0 }} alignItems={"flex-start"} pt={{base: '20px', md: '56px'}} pb={{base: '28px', md: '56px'}} justifyContent={"space-between"}>
            <Flex w={"100%"} gap={{ base: '16px', md: '40px' }} flexDirection={{base: 'column', md: 'row'}}>
                <Flex w={"100%"} gap={{ base: '16px', md: '40px' }} flexDirection={{base: 'column', md: 'row'}} alignItems={{ base: 'stretch', md: 'center' }}>
                    {event?.coverImage ? (
                        <Image src={getImg(event)} alt={event.title} objectFit="contain" bg="#F8ECEF"
                               w="100%" maxW={{ base: '100%', md: '400px' }} flexShrink={0}
                               aspectRatio="16 / 9" h="auto" borderRadius={{ base: '18px', md: '20px' }}
                               boxShadow="0 12px 32px rgba(14,26,23,0.10)"
                               cursor="pointer" onClick={onImageClick}/>
                    ) : (
                        <Box w="100%" maxW={{ base: '100%', md: '400px' }} flexShrink={0} aspectRatio="16 / 9" borderRadius="20px" overflow="hidden"><NoCover size="100%" h="100%" minH={0} /></Box>
                    )}
                    <Flex flexDirection={'column'} gap={{ base: '16px', md: '18px' }} flex="1" minW={0}>
                        <Flex w="100%" justifyContent="space-between" alignItems="center" gap="16px">
                            <Flex gap={'8px'} alignItems={"center"} flexWrap="wrap">
                                {event?.categories?.map((category, index) => (
                                    <CategoryTag key={index}>{category}</CategoryTag>
                                ))}
                            </Flex>
                            <Box display={{base: 'block', md: 'none'}}>
                                {socialButtons}
                            </Box>
                        </Flex>
                        <Heading as="h1" onClick={() => handleEvents()} cursor={"pointer"} fontWeight={700} fontSize={{base: '32px', md: '48px'}} lineHeight={{ base: 1.08, md: 1.05 }} letterSpacing={{ base: '-0.6px', md: '-1px' }} wordBreak="break-word">
                            {event?.title}
                        </Heading>
                        <Flex flexDirection={'column'} gap="12px" fontSize={{ base: '15px', md: '16px' }} lineHeight="1.45">
                            <Flex gap="12px" alignItems="center">
                                <IconBadge>
                                    {event?.isOnline ? (
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7A1F35" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="2" y1="12" x2="22" y2="12"></line><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path></svg>
                                    ) : (
                                        <svg width="18" height="18" viewBox="0 0 18 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M14.657 14.9139L10.414 19.1569C10.2284 19.3427 10.0081 19.49 9.76556 19.5905C9.52303 19.6911 9.26305 19.7428 9.0005 19.7428C8.73796 19.7428 8.47798 19.6911 8.23544 19.5905C7.99291 19.49 7.77256 19.3427 7.587 19.1569L3.343 14.9139C2.22422 13.7951 1.46234 12.3696 1.15369 10.8178C0.845043 9.26594 1.00349 7.65742 1.60901 6.19564C2.21452 4.73385 3.2399 3.48445 4.55548 2.60542C5.87107 1.72638 7.41777 1.2572 9 1.2572C10.5822 1.2572 12.1289 1.72638 13.4445 2.60542C14.7601 3.48445 15.7855 4.73385 16.391 6.19564C16.9965 7.65742 17.155 9.26594 16.8463 10.8178C16.5377 12.3696 15.7758 13.7951 14.657 14.9139Z" stroke="#7A1F35" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M12 9.2569C12 10.0525 11.6839 10.8156 11.1213 11.3782C10.5587 11.9408 9.79565 12.2569 9 12.2569C8.20435 12.2569 7.44129 11.9408 6.87868 11.3782C6.31607 10.8156 6 10.0525 6 9.2569C6 8.46125 6.31607 7.69819 6.87868 7.13558C7.44129 6.57297 8.20435 6.2569 9 6.2569C9.79565 6.2569 10.5587 6.57297 11.1213 7.13558C11.6839 7.69819 12 8.46125 12 9.2569Z" stroke="#7A1F35" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    )}
                                </IconBadge>
                                <Text>{event?.isOnline ? `${event?.platform || ''} ${t('event.online', '(Wydarzenie online)')}` : event?.address}</Text>
                            </Flex>
                            <Flex gap="12px" alignItems="center">
                                <IconBadge>
                                    <svg width="18" height="18" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M10 6.5V10.5L13 13.5M19 10.5C19 11.6819 18.7672 12.8522 18.3149 13.9442C17.8626 15.0361 17.1997 16.0282 16.364 16.864C15.5282 17.6997 14.5361 18.3626 13.4442 18.8149C12.3522 19.2672 11.1819 19.5 10 19.5C8.8181 19.5 7.64778 19.2672 6.55585 18.8149C5.46392 18.3626 4.47177 17.6997 3.63604 16.864C2.80031 16.0282 2.13738 15.0361 1.68508 13.9442C1.23279 12.8522 1 11.6819 1 10.5C1 8.11305 1.94821 5.82387 3.63604 4.13604C5.32387 2.44821 7.61305 1.5 10 1.5C12.3869 1.5 14.6761 2.44821 16.364 4.13604C18.0518 5.82387 19 8.11305 19 10.5Z" stroke="#7A1F35" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                </IconBadge>
                                <Text>{eventDate(event)}</Text>
                            </Flex>
                            <Flex gap="12px" alignItems="center">
                                <IconBadge>
                                    <svg width="18" height="18" viewBox="0 0 21 17" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M13.5 1.5V3.5M13.5 7.5V9.5M13.5 13.5V15.5M3.5 1.5C2.96957 1.5 2.46086 1.71071 2.08579 2.08579C1.71071 2.46086 1.5 2.96957 1.5 3.5V6.5C2.03043 6.5 2.53914 6.71071 2.91421 7.08579C3.28929 7.46086 3.5 7.96957 3.5 8.5C3.5 9.03043 3.28929 9.53914 2.91421 9.91421C2.53914 10.2893 2.03043 10.5 1.5 10.5V13.5C1.5 14.0304 1.71071 14.5391 2.08579 14.9142C2.46086 15.2893 2.96957 15.5 3.5 15.5H17.5C18.0304 15.5 18.5391 15.2893 18.9142 14.9142C19.2893 14.5391 19.5 14.0304 19.5 13.5V10.5C18.9696 10.5 18.4609 10.2893 18.0858 9.91421C17.7107 9.53914 17.5 9.03043 17.5 8.5C17.5 7.96957 17.7107 7.46086 18.0858 7.08579C18.4609 6.71071 18.9696 6.5 19.5 6.5V3.5C19.5 2.96957 19.2893 2.46086 18.9142 2.08579C18.5391 1.71071 18.0304 1.5 17.5 1.5H3.5Z" stroke="#7A1F35" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                </IconBadge>
                                <Text>{getPriceLabel(event, t, i18n.language)}</Text>
                            </Flex>
                        </Flex>
                    </Flex>
                </Flex>
                <Box display={{base: 'none', md: 'block'}}>
                    {socialButtons}
                </Box>
            </Flex>
            {onBuyClick && event.status !== 'Cancelled' && (
                <Box display={{base: 'block', md: 'none'}} w="100%">
                    <BuyTicketButton event={event} onClick={onBuyClick} alwaysShowFromLabel />
                </Box>
            )}
        </Flex>
    </Flex>
};

export default EventDetailHeaderPage;
